import { useState, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	Box,
	Divider,
	Chip,
	CircularProgress,
	FormHelperText,
} from "@mui/material";
import { CalendarMonth, DateRange } from "@mui/icons-material";
import { format, addDays, endOfMonth } from "date-fns";
import { useAutoCreatePayPeriod } from "../../../../../../services/pay-period.service";
import {
	AutoCreatePayPeriodDto,
	PAY_PERIOD_TIME_TYPE,
} from "../../../../../../models/pay-period.dto";
import useCompanyStore from "../../../../../../state-management/useCompanyStore";
import useUserStore from "../../../../../../state-management/useUserStore";

// PAY_PERIOD_TIME_TYPE is a plain const object — derive a union from its values
type TimePeriodValue =
	(typeof PAY_PERIOD_TIME_TYPE)[keyof typeof PAY_PERIOD_TIME_TYPE];

// ─── Props ────────────────────────────────────────────────────────────────────

interface AutoGeneratePayPeriodsModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess?: () => void;
}

// ─── Preview row shape ────────────────────────────────────────────────────────

interface PreviewRow {
	name: string;
	startDate: string;
	endDate: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();

const MONTH_LABELS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

// Max day 28 keeps all options valid across every month (incl. February)
const DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);

// ─── Preview helpers ──────────────────────────────────────────────────────────

function clampDay(day: number, year: number, month: number) {
	return Math.min(day, new Date(year, month + 1, 0).getDate());
}

function buildMonthlyPreview(year: number, startDay: number): PreviewRow[] {
	return Array.from({ length: 12 }, (_, m) => {
		const start = new Date(year, m, clampDay(startDay, year, m));
		return {
			name: `${MONTH_LABELS[m]} ${year}`,
			startDate: format(start, "MMM d"),
			endDate: format(endOfMonth(start), "MMM d, yyyy"),
		};
	});
}

function buildFortnightlyPreview(
	year: number,
	firstStart: number,
	secondStart: number,
): PreviewRow[] {
	// Show first 4 months as a sample (8 of 24 periods)
	const rows: PreviewRow[] = [];
	for (let m = 0; m < 4; m++) {
		const d1 = clampDay(firstStart, year, m);
		const d2 = clampDay(secondStart, year, m);
		rows.push(
			{
				name: `${MONTH_LABELS[m]} ${year} – Period 1`,
				startDate: format(new Date(year, m, d1), "MMM d"),
				endDate: format(addDays(new Date(year, m, d2), -1), "MMM d"),
			},
			{
				name: `${MONTH_LABELS[m]} ${year} – Period 2`,
				startDate: format(new Date(year, m, d2), "MMM d"),
				endDate: format(endOfMonth(new Date(year, m, d2)), "MMM d, yyyy"),
			},
		);
	}
	return rows;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AutoGeneratePayPeriodsModal = ({
	open,
	setOpen,
	onSuccess,
}: AutoGeneratePayPeriodsModalProps) => {
	const { currentCompany } = useCompanyStore();
	const { currentUser } = useUserStore();

	const [timePeriod, setTimePeriod] = useState<TimePeriodValue>(
		PAY_PERIOD_TIME_TYPE.MONTHLY,
	);
	const [firstStartDate, setFirstStartDate] = useState<number>(1);
	const [secondStartDate, setSecondStartDate] = useState<number>(16);

	const { mutate: autoCreate, isLoading } = useAutoCreatePayPeriod();

	const isMonthly = timePeriod === PAY_PERIOD_TIME_TYPE.MONTHLY;
	const sequenceNumber = isMonthly ? 12 : 24;

	const fortnightlyError =
		!isMonthly && secondStartDate <= firstStartDate
			? "Second start day must be after the first"
			: null;

	const preview = useMemo<PreviewRow[]>(() => {
		return isMonthly
			? buildMonthlyPreview(CURRENT_YEAR, firstStartDate)
			: buildFortnightlyPreview(CURRENT_YEAR, firstStartDate, secondStartDate);
	}, [isMonthly, firstStartDate, secondStartDate]);

	const handleClose = () => {
		if (isLoading) return;
		setOpen(false);
	};

	const handleGenerate = () => {
		if (fortnightlyError || !currentCompany?.id) return;

		// sequenceNumber is defined on AutoCreatePayPeriodDto in the frontend
		// model but the backend endpoint does not accept it — omit it from the
		// payload to avoid a REQUEST_VALIDATION_FAILED error.
		const payload = {
			organizationId: currentUser.user.organizationId,
			companyId: currentCompany.id,
			timePeriod: timePeriod as AutoCreatePayPeriodDto["timePeriod"],
			firstStartDate,
			...(!isMonthly && { secondStartDate }),
		};

		autoCreate(
			{ payload: payload as AutoCreatePayPeriodDto },
			{
				onSuccess: () => {
					onSuccess?.();
					setOpen(false);
				},
			},
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { borderRadius: 3 } }}
		>
			{/* ── Header ── */}
			<DialogTitle sx={{ pb: 1 }}>
				<Box display="flex" alignItems="center" gap={1.5}>
					<CalendarMonth sx={{ color: "primary.main" }} />
					<Box>
						<Typography variant="h6" fontWeight={600} lineHeight={1.2}>
							Auto-Generate Pay Periods
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Bulk-create all periods for a full year in one step
						</Typography>
					</Box>
				</Box>
			</DialogTitle>

			<Divider />

			<DialogContent
				sx={{ pt: 3, pb: 2, display: "flex", flexDirection: "column", gap: 3 }}
			>
				{/* ── Frequency ── */}
				<Box>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ mb: 0.75, display: "block" }}
					>
						Frequency
					</Typography>
					<ToggleButtonGroup
						exclusive
						value={timePeriod}
						onChange={(_, val) => val && setTimePeriod(val)}
						size="small"
						disabled={isLoading}
						fullWidth
					>
						<ToggleButton
							value={PAY_PERIOD_TIME_TYPE.MONTHLY}
							sx={{ textTransform: "none", py: 1.2 }}
						>
							<CalendarMonth sx={{ fontSize: 16, mr: 0.8 }} />
							Monthly
							<Chip
								label="12 periods"
								size="small"
								sx={{ ml: 1, height: 18, fontSize: 10 }}
							/>
						</ToggleButton>
						<ToggleButton
							value={PAY_PERIOD_TIME_TYPE.FORTNIGHTLY}
							sx={{ textTransform: "none", py: 1.2 }}
						>
							<DateRange sx={{ fontSize: 16, mr: 0.8 }} />
							Fortnightly
							<Chip
								label="24 periods"
								size="small"
								sx={{ ml: 1, height: 18, fontSize: 10 }}
							/>
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>

				{/* ── Frequency + start days ── */}
				<Box display="flex" gap={2}>
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
						}}
					>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ mb: 0.5 }}
						>
							Year
						</Typography>
						<Typography variant="body1" fontWeight={600}>
							{CURRENT_YEAR}
						</Typography>
					</Box>

					<FormControl size="small" sx={{ flex: 1 }}>
						<InputLabel>
							{isMonthly ? "Start Day" : "1st Period Start Day"}
						</InputLabel>
						<Select
							label={isMonthly ? "Start Day" : "1st Period Start Day"}
							value={firstStartDate}
							onChange={(e) => setFirstStartDate(Number(e.target.value))}
							disabled={isLoading}
						>
							{DAY_OPTIONS.map((d) => (
								<MenuItem key={d} value={d}>
									Day {d}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>
							{isMonthly ? "Day each period begins" : "Start of first half"}
						</FormHelperText>
					</FormControl>

					{!isMonthly && (
						<FormControl
							size="small"
							sx={{ flex: 1 }}
							error={!!fortnightlyError}
						>
							<InputLabel>2nd Period Start Day</InputLabel>
							<Select
								label="2nd Period Start Day"
								value={secondStartDate}
								onChange={(e) => setSecondStartDate(Number(e.target.value))}
								disabled={isLoading}
							>
								{DAY_OPTIONS.map((d) => (
									<MenuItem key={d} value={d}>
										Day {d}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								{fortnightlyError ?? "Start of second half"}
							</FormHelperText>
						</FormControl>
					)}
				</Box>

				{/* ── Preview ── */}
				<Box>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ mb: 1, display: "block" }}
					>
						{isMonthly
							? "Preview — all 12 periods"
							: "Preview — first 4 months (8 of 24 periods)"}
					</Typography>

					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							overflow: "hidden",
						}}
					>
						{/* Table header */}
						<Box
							display="grid"
							gridTemplateColumns="1fr 100px 100px"
							sx={{
								bgcolor: "grey.50",
								px: 2,
								py: 1,
								borderBottom: "1px solid",
								borderColor: "divider",
							}}
						>
							{["Period Name", "Start", "End"].map((h) => (
								<Typography
									key={h}
									variant="caption"
									fontWeight={600}
									color="text.secondary"
								>
									{h}
								</Typography>
							))}
						</Box>

						{/* Table rows */}
						<Box sx={{ maxHeight: 240, overflowY: "auto" }}>
							{preview.map((p, i) => (
								<Box
									key={i}
									display="grid"
									gridTemplateColumns="1fr 100px 100px"
									sx={{
										px: 2,
										py: 1,
										borderBottom: i < preview.length - 1 ? "1px solid" : "none",
										borderColor: "divider",
										"&:hover": { bgcolor: "grey.50" },
									}}
								>
									<Typography variant="body2">{p.name}</Typography>
									<Typography variant="body2" color="text.secondary">
										{p.startDate}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{p.endDate}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>

					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ mt: 0.75, display: "block" }}
					>
						Exact period names and end dates are determined server-side.
					</Typography>
				</Box>
			</DialogContent>

			<Divider />

			{/* ── Actions ── */}
			<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
				<Button
					variant="outlined"
					onClick={handleClose}
					disabled={isLoading}
					sx={{ textTransform: "none" }}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleGenerate}
					disabled={isLoading || !!fortnightlyError}
					startIcon={
						isLoading ? (
							<CircularProgress size={16} color="inherit" />
						) : undefined
					}
					sx={{ textTransform: "none", minWidth: 160 }}
				>
					{isLoading ? "Generating…" : `Generate ${sequenceNumber} Periods`}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AutoGeneratePayPeriodsModal;
